// Portal design tokens. The same names and values as the mobile app's
// mobile-app/src/common/theme.js tokens, so the portal and the apps share one
// colour language. Contrast figures are against white unless noted.
//
// Rules (owner decisions, wave 2):
// - Orange buttons with white labels use BUTTON_PRIMARY #C73D0E (5.1:1).
//   ACCENT #F1501A stays for the header bar, icons, tints and selected
//   outlines; white text sits on it only at >= 19 px bold (3.56:1).
// - Small orange text and links use ACCENT_TEXT (the same #C73D0E).
// - The green "go" kind (SUCCESS #0B7A14, white 5.5:1) is for Accept, Start
//   trip, Find driver, Add customer and Call driver.
// - Status chips are dark text on a pale tint, always with an icon and a word.

export const tokens = {
  // Text
  INK: '#1F2937',          // 14.7:1 primary text
  INK_2: '#4B5563',        // 7.6:1 secondary text, labels
  TEXT_3: '#5F6672',       // 5.8:1 captions, timestamps, section labels
  PLACEHOLDER_TEXT: '#7A7F87', // 4.0:1 placeholders and disabled only

  // Brand
  ACCENT: '#F1501A',       // bar, icons, tints, selected outlines
  ACCENT_TEXT: '#C73D0E',  // small orange text and links (4.7:1 on ACCENT_TINT)
  ACCENT_TINT: '#FFF4EF',
  BUTTON_PRIMARY: '#C73D0E',         // contained primary buttons, white label 5.1:1
  BUTTON_PRIMARY_PRESSED: '#AF360C', // hover / pressed fill

  // Status (text colour, then its tint)
  SUCCESS: '#0B7A14',      // green "go" fill, white label 5.5:1
  SUCCESS_PRESSED: '#09640F',
  SUCCESS_TEXT: '#15803D',
  SUCCESS_TINT: '#ECFDF3',
  DANGER: '#DC2626',       // outlines of danger buttons, white label 4.8:1
  DANGER_TEXT: '#B91C1C',  // error text, badges, filled destructive buttons (6.5:1)
  DANGER_TINT: '#FEF2F2',
  WARNING_TEXT: '#B45309',
  WARNING_TINT: '#FFF7ED',
  AMBER_TEXT: '#92400E',
  AMBER_TINT: '#FEF3C7',
  ESTIMATE_TINT: '#FFFBEB', // the dashed "≈ Estimate" chip
  INFO_TEXT: '#1D4ED8',
  INFO_TINT: '#EFF6FF',
  // Icon hues (owner, 28 Oct: tell the Dashboard cards apart by colour, as
  // Apple's Settings does): the glyph colour on its own tint, 6.5:1 and 5.2:1
  // (icons need 3:1).
  VIOLET_TEXT: '#6D28D9',
  VIOLET_TINT: '#F5F3FF',
  TEAL_TEXT: '#0F766E',
  TEAL_TINT: '#F0FDFA',

  // Surfaces and lines
  SCREEN_BG: '#F5F6F8',
  SURFACE: '#FFFFFF',
  SURFACE_ALT: '#F0F1F3',
  OUTLINE: '#ECEEF1',
  OUTLINE_STRONG: '#D6D9DE',
  SKELETON: '#E9ECF0',
  SCRIM: 'rgba(17,24,39,0.45)',
  WHITE: '#FFFFFF',
};

// Shared sizes for the portal (px). Controls are 40 high for a mouse and 44
// on a touch screen; the app bar is 56 and the sidebar 220.
export const layout = {
  APP_BAR_HEIGHT: 56,
  SIDEBAR_WIDTH: 220,
  CONTROL_HEIGHT: 40,
  TOUCH_TARGET: 44,
  RADIUS_CONTROL: 10,
  RADIUS_CARD: 12,
};

// One tone per kind of message or status: { fg, bg }. Text on its tint is at
// least 4.7:1. Use with an icon and a word, never colour alone.
export const tones = {
  success: { fg: tokens.SUCCESS_TEXT, bg: tokens.SUCCESS_TINT },
  error: { fg: tokens.DANGER_TEXT, bg: tokens.DANGER_TINT },
  warning: { fg: tokens.WARNING_TEXT, bg: tokens.WARNING_TINT },
  amber: { fg: tokens.AMBER_TEXT, bg: tokens.AMBER_TINT },
  info: { fg: tokens.INFO_TEXT, bg: tokens.INFO_TINT },
  accent: { fg: tokens.ACCENT_TEXT, bg: tokens.ACCENT_TINT },
  neutral: { fg: tokens.INK_2, bg: tokens.SURFACE_ALT },
};

// Booking status -> tone, as drawn on the approved portal boards: new and
// waiting states amber-orange, live trip states blue, finished green,
// cancelled red.
const BOOKING_STATUS_TONE = {
  NEW: 'warning',
  PENDING: 'warning',
  PAYMENT_PENDING: 'warning',
  ACCEPTED: 'info',
  ARRIVED: 'info',
  STARTED: 'info',
  REACHED: 'info',
  COMPLETE: 'success',
  PAID: 'success',
  CANCELLED: 'error',
};

export const bookingStatusTone = (status) => {
  const key = typeof status === 'string' ? status.toUpperCase() : '';
  return tones[BOOKING_STATUS_TONE[key] || 'neutral'];
};

// sx for a dark-on-tint chip or badge of one tone (MUI <Chip sx={toneChipSx('success')} />).
export const toneChipSx = (toneName) => {
  const tone = tones[toneName] || tones.neutral;
  return {
    color: tone.fg,
    backgroundColor: tone.bg,
    fontWeight: 600,
    '& .MuiChip-icon': { color: tone.fg },
  };
};

// Icon-on-a-tinted-circle hues used by the sidebar and list rows.
export const iconHues = {
  accent: { fg: tokens.ACCENT_TEXT, bg: tokens.ACCENT_TINT },
  info: { fg: tokens.INFO_TEXT, bg: tokens.INFO_TINT },
  success: { fg: tokens.SUCCESS_TEXT, bg: tokens.SUCCESS_TINT },
  danger: { fg: tokens.DANGER_TEXT, bg: tokens.DANGER_TINT },
  amber: { fg: tokens.AMBER_TEXT, bg: tokens.AMBER_TINT },
  neutral: { fg: tokens.INK_2, bg: tokens.SURFACE_ALT },
  violet: { fg: tokens.VIOLET_TEXT, bg: tokens.VIOLET_TINT },
  teal: { fg: tokens.TEAL_TEXT, bg: tokens.TEAL_TINT },
};
